import React, { useMemo, useState } from 'react';
import { View } from 'react-native';
import Svg, { Path, Circle, Text as SvgText, Line, G, Defs, LinearGradient, Stop } from 'react-native-svg';
import { Text } from '@/components/gluestack/text';
import { useTheme } from '@/components/providers/ThemeProvider';
import { WeightChartPoint } from '@/models/Weight';
import { KG_TO_LBS, KG_TO_ST, type WeightUnit } from '~/components/providers/MeasurementsProvider';
import dayjs from '@/i18n/dayjs-config';

function convertWeight(kg: number, unit: WeightUnit): number {
  if (unit === 'kg') return Math.round(kg);
  if (unit === 'st') return parseFloat((kg * KG_TO_ST).toFixed(1));
  return Math.round(kg * KG_TO_LBS);
}

function formatWeightLabel(kg: number, unit: WeightUnit): string {
  const val = convertWeight(kg, unit);
  return String(val);
}

function formatChartLabel(date: Date, granularity: string): string {
  const d = dayjs.utc(date);
  switch (granularity) {
    case 'day':
    case 'week':
      return d.format('D MMM');
    case 'month':
      return d.format("MMM [']YY");
    case 'year':
      return d.format('YYYY');
    default:
      return d.format('D MMM');
  }
}

interface WeightChartProps {
  points: WeightChartPoint[];
  granularity: string;
  weightUnit: WeightUnit;
  emptyMessage?: string;
}

const CHART_HEIGHT = 238;
const PADDING_TOP = 16;
const PADDING_BOTTOM = 70;
const GRID_INSET = 6;
const PADDING_LEFT = 30;
const PADDING_RIGHT = 20;
const DOT_RADIUS = 5;
const MIN_LABEL_SPACING = 38;

export function WeightChart({ points, granularity, weightUnit, emptyMessage }: WeightChartProps) {
  const { getThemeColor, effectiveColorScheme } = useTheme();
  const [containerWidth, setContainerWidth] = useState(0);

  const primaryColor = getThemeColor('primary', '500');
  const gridColor = effectiveColorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const labelColor = effectiveColorScheme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)';
  const valueLabelColor = effectiveColorScheme === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)';

  const chartData = useMemo(() => {
    if (!points || points.length === 0 || containerWidth <= 0) return null;

    const nonNullPoints = points.filter((p) => p.AvgWeightKg != null);
    if (nonNullPoints.length === 0) return null;

    const convertedValues = nonNullPoints.map((p) => convertWeight(p.AvgWeightKg!, weightUnit));
    const minVal = Math.min(...convertedValues);
    const maxVal = Math.max(...convertedValues);
    const range = maxVal - minVal;
    const padding = range === 0 ? Math.max(5, minVal * 0.05) : range * 0.15;
    const yMin = minVal - padding;
    const yMax = maxVal + padding;

    const drawableHeight = CHART_HEIGHT - PADDING_TOP - PADDING_BOTTOM;
    const svgWidth = containerWidth;

    const getX = (index: number) => {
      if (points.length <= 1) return svgWidth / 2;
      return PADDING_LEFT + (index * (svgWidth - PADDING_LEFT - PADDING_RIGHT)) / (points.length - 1);
    };
    const getY = (val: number) => {
      if (yMax === yMin) return PADDING_TOP + drawableHeight / 2;
      return PADDING_TOP + drawableHeight - ((val - yMin) / (yMax - yMin)) * drawableHeight;
    };

    // Build data points with screen coordinates
    const dataPoints = points.map((p, i) => ({
      x: getX(i),
      y: p.AvgWeightKg != null ? getY(convertWeight(p.AvgWeightKg, weightUnit)) : null,
      value: p.AvgWeightKg != null ? convertWeight(p.AvgWeightKg, weightUnit) : null,
      label: formatChartLabel(p.StartDate, granularity),
      isNull: p.AvgWeightKg == null,
    }));

    // Build path segments (solid for consecutive values, dashed for gaps)
    const segments: { path: string; dashed: boolean }[] = [];
    let i = 0;
    while (i < dataPoints.length) {
      if (dataPoints[i].isNull) {
        i++;
        continue;
      }
      const solidStart = i;
      while (i < dataPoints.length && !dataPoints[i].isNull) {
        i++;
      }
      const solidEnd = i - 1;

      if (solidStart <= solidEnd) {
        let d = `M ${dataPoints[solidStart].x} ${dataPoints[solidStart].y}`;
        for (let j = solidStart + 1; j <= solidEnd; j++) {
          d += ` L ${dataPoints[j].x} ${dataPoints[j].y}`;
        }
        segments.push({ path: d, dashed: false });
      }

      if (i < dataPoints.length) {
        const gapStart = solidEnd;
        while (i < dataPoints.length && dataPoints[i].isNull) {
          i++;
        }
        if (i < dataPoints.length && !dataPoints[i].isNull) {
          const d = `M ${dataPoints[gapStart].x} ${dataPoints[gapStart].y} L ${dataPoints[i].x} ${dataPoints[i].y}`;
          segments.push({ path: d, dashed: true });
        }
      }
    }

    // Compute Y-axis grid lines
    const gridCount = 4;
    const gridLines: { y: number; label: string }[] = [];
    for (let g = 0; g <= gridCount; g++) {
      const val = yMin + (g * (yMax - yMin)) / gridCount;
      gridLines.push({
        y: getY(val),
        label: String(Math.round(val)),
      });
    }

    // Build gradient fill path
    const nonNullDataPoints = dataPoints.filter((p) => !p.isNull);
    let fillPath = '';
    if (nonNullDataPoints.length >= 2) {
      fillPath = `M ${nonNullDataPoints[0].x} ${nonNullDataPoints[0].y}`;
      for (let j = 1; j < nonNullDataPoints.length; j++) {
        fillPath += ` L ${nonNullDataPoints[j].x} ${nonNullDataPoints[j].y}`;
      }
      const bottomY = CHART_HEIGHT - PADDING_BOTTOM;
      fillPath += ` L ${nonNullDataPoints[nonNullDataPoints.length - 1].x} ${bottomY}`;
      fillPath += ` L ${nonNullDataPoints[0].x} ${bottomY} Z`;
    }

    // Compute which x-axis labels to show (skip to avoid overlap)
    const drawableWidth = svgWidth - PADDING_LEFT - PADDING_RIGHT;
    const spacingPerPoint = points.length > 1 ? drawableWidth / (points.length - 1) : drawableWidth;
    let labelStep = 1;
    if (spacingPerPoint < MIN_LABEL_SPACING) {
      labelStep = Math.ceil(MIN_LABEL_SPACING / spacingPerPoint);
    }
    const visibleLabelIndices = new Set<number>();
    // Always show first and last
    visibleLabelIndices.add(0);
    visibleLabelIndices.add(points.length - 1);
    for (let idx = 0; idx < points.length; idx += labelStep) {
      visibleLabelIndices.add(idx);
    }

    // Compute year span label
    const years = nonNullPoints.map((p) => dayjs.utc(p.StartDate).year());
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    const yearSpan = minYear === maxYear ? String(minYear) : `${minYear}–${maxYear}`;

    return { svgWidth, dataPoints, segments, gridLines, fillPath, visibleLabelIndices, yearSpan };
  }, [points, granularity, weightUnit, containerWidth]);

  if ((!chartData && containerWidth > 0) || !points || points.length === 0) {
    return (
      <View
        className="w-full items-center justify-center py-12"
        onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}>
        <Text size="sm" className="text-typography-500">
          {emptyMessage || 'No weight data'}
        </Text>
      </View>
    );
  }

  if (!chartData) {
    return (
      <View
        className="w-full"
        style={{ height: CHART_HEIGHT }}
        onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
      />
    );
  }

  const { svgWidth, dataPoints, segments, gridLines, fillPath, visibleLabelIndices, yearSpan } = chartData;

  return (
    <View
      className="w-full"
      style={{ height: CHART_HEIGHT }}
      onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}>
      {containerWidth > 0 ? (
        <Svg width={svgWidth} height={CHART_HEIGHT}>
          <Defs>
            <LinearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
              <Stop offset="0" stopColor={primaryColor} stopOpacity="0.15" />
              <Stop offset="1" stopColor={primaryColor} stopOpacity="0.01" />
            </LinearGradient>
          </Defs>

          {/* Grid lines */}
          {gridLines.map((line, idx) => (
            <Line
              key={`grid-${idx}`}
              x1={GRID_INSET}
              y1={line.y}
              x2={svgWidth - GRID_INSET}
              y2={line.y}
              stroke={gridColor}
              strokeWidth={1}
            />
          ))}

          {/* Fill area under the curve */}
          {fillPath ? (
            <Path d={fillPath} fill="url(#areaGradient)" />
          ) : null}

          {/* Line segments */}
          {segments.map((seg, idx) => (
            <Path
              key={`seg-${idx}`}
              d={seg.path}
              stroke={primaryColor}
              strokeWidth={2.5}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              {...(seg.dashed ? { strokeDasharray: '6,4' } : {})}
            />
          ))}

          {/* Data point dots and value labels */}
          {dataPoints.map((pt, idx) =>
            pt.y != null && pt.value != null ? (
              <G key={`dot-${idx}`}>
                {/* Outer glow */}
                <Circle
                  cx={pt.x}
                  cy={pt.y}
                  r={DOT_RADIUS + 3}
                  fill={primaryColor}
                  opacity={0.12}
                />
                {/* Dot */}
                <Circle
                  cx={pt.x}
                  cy={pt.y}
                  r={DOT_RADIUS}
                  fill={primaryColor}
                  stroke={effectiveColorScheme === 'dark' ? '#1a1a1a' : '#fff'}
                  strokeWidth={2}
                />
                {/* Value label above dot */}
                <SvgText
                  x={pt.x}
                  y={pt.y - 14}
                  textAnchor="middle"
                  fontSize={12}
                  fontWeight="600"
                  fill={valueLabelColor}>
                  {formatWeightLabel(points[idx].AvgWeightKg!, weightUnit)}
                </SvgText>
              </G>
            ) : null
          )}

          {/* X-axis labels (rotated 45deg, with skipping for density) */}
          {dataPoints.map((pt, idx) =>
            visibleLabelIndices.has(idx) ? (
              <SvgText
                key={`xlabel-${idx}`}
                x={pt.x}
                y={CHART_HEIGHT - PADDING_BOTTOM + 14}
                textAnchor="end"
                fontSize={10}
                fill={labelColor}
                rotation={-45}
                originX={pt.x}
                originY={CHART_HEIGHT - PADDING_BOTTOM + 14}>
                {pt.label}
              </SvgText>
            ) : null
          )}

          {/* Year span label centered at bottom */}
          <SvgText
            x={svgWidth / 2}
            y={CHART_HEIGHT - 6}
            textAnchor="middle"
            fontSize={11}
            fill={labelColor}>
            {yearSpan}
          </SvgText>
        </Svg>
      ) : null}
    </View>
  );
}
